// frontend/src/components/admin/Exploitation.jsx
// ============================================================================
// EXPLOITATION
// ----------------------------------------------------------------------------
// L'etat d'HoraPilot sans ouvrir de session SSH : le serveur applicatif, la
// base, le disque, la derniere sauvegarde, et la structure de la base
// (migrations appliquees, en attente, en echec).
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  Activity, RefreshCw, Server, Database, HardDrive, DatabaseBackup,
  ListChecks, CheckCircle2, AlertTriangle, Clock, Play
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const FUSEAU = 'Africa/Brazzaville';

const poids = (octets) => {
  if (!Number.isFinite(octets)) return '—';
  if (octets >= 1073741824) return `${(octets / 1073741824).toFixed(1)} Go`;
  if (octets >= 1048576) return `${Math.round(octets / 1048576)} Mo`;
  if (octets >= 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${octets} o`;
};

const nombre = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('fr-FR') : '—');

const quand = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: FUSEAU, day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }).format(d);
};

const Panneau = ({ Icon, titre, couleur, t, enfants, coin }) => (
  <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
    overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
    <div style={{ padding: '13px 18px', borderBottom: `1px solid ${t.borderLight}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 15,
        fontWeight: 700, color: t.text }}>
        <Icon size={16} style={{ color: couleur }} />
        {titre}
      </span>
      {coin}
    </div>
    <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 9 }}>
      {enfants}
    </div>
  </div>
);

const Ligne = ({ libelle, valeur, ton, t }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14 }}>
    <span style={{ fontSize: 13, color: t.textMuted }}>{libelle}</span>
    <span style={{ fontSize: 14, fontWeight: 600, textAlign: 'right',
      color: ton === 'danger' ? t.dangerText : ton === 'succes' ? t.successText : t.text }}>
      {valeur}
    </span>
  </div>
);

const Exploitation = () => {
  const t = useTheme();
  const [etat, setEtat] = useState(null);
  const [migrations, setMigrations] = useState([]);
  const [jeux, setJeux] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [application, setApplication] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const [e, m, s] = await Promise.all([
        superAdminApi.exploitation.etat(),
        superAdminApi.exploitation.migrations(),
        superAdminApi.exploitation.sauvegardes()
      ]);
      setEtat(e.data || null);
      setMigrations(m.data || []);
      setJeux(s.data || null);
    } catch (err) {
      setErreur(err.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const appliquer = async () => {
    setApplication(true);
    setMessage(null);
    try {
      const r = await superAdminApi.exploitation.appliquerMigrations();
      const cr = r.data || {};
      setMessage(cr.echec
        ? `Échec sur ${cr.echec.nom} : ${cr.echec.message}`
        : `${cr.appliquees?.length || 0} migration(s) appliquée(s).`);
      await charger();
    } catch (err) {
      setMessage(err.message || 'Application impossible');
    } finally {
      setApplication(false);
    }
  };

  const enAttente = migrations.filter((m) => m.statut === 'en_attente').length;
  const enEchec = migrations.filter((m) => m.statut === 'echec').length;

  const s = etat?.serveur;
  const b = etat?.base;
  const d = etat?.disque;
  const sv = etat?.sauvegardes;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <Activity size={20} style={{ color: t.primary }} />
            Exploitation
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            {etat ? `Mesuré le ${quand(etat.mesure_le)}` : 'Lecture en cours…'}
          </p>
        </div>
        <button type="button" onClick={charger}
          style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
            padding: '9px 14px', fontSize: 13, fontWeight: 600, color: t.text, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
          <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
          Actualiser
        </button>
      </div>

      {erreur && (
        <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 14,
          padding: '14px 18px', fontSize: 14, color: t.dangerText }}>{erreur}</div>
      )}

      {etat && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }}>

          <Panneau Icon={Server} titre="Serveur applicatif" couleur={t.primary} t={t} enfants={<>
            <Ligne libelle="Node" valeur={s.node} t={t} />
            <Ligne libelle="Environnement" valeur={s.environnement} t={t} />
            <Ligne libelle="En route depuis" valeur={s.depuis} t={t} />
            <Ligne libelle="Mémoire du processus" valeur={poids(s.memoire_utilisee)} t={t} />
            <Ligne libelle="Mémoire libre / machine"
              valeur={`${poids(s.memoire_libre)} / ${poids(s.memoire_machine)}`} t={t} />
            <Ligne libelle="Charge (1, 5, 15 min)" valeur={s.charge.join('  ·  ')} t={t} />
            <Ligne libelle="Fuseau" valeur={s.fuseau} t={t} />
          </>} />

          <Panneau Icon={Database} titre="Base de données" couleur={t.primary} t={t} enfants={<>
            <Ligne libelle="MySQL" valeur={b.version} t={t} />
            <Ligne libelle="En route depuis" valeur={b.depuis} t={t} />
            <Ligne libelle="Taille" valeur={`${poids(b.taille_octets)} · ${b.tables} tables`} t={t} />
            <Ligne libelle="Connexions ouvertes" valeur={b.connexions_ouvertes} t={t} />
            <Ligne libelle="Requêtes lentes" valeur={nombre(b.requetes_lentes)}
              ton={b.requetes_lentes > 0 ? 'danger' : undefined} t={t} />
            <Ligne libelle="Connexions refusées" valeur={nombre(b.connexions_refusees)} t={t} />
            <Ligne libelle="Plus grosse table"
              valeur={b.plus_grosses?.[0]
                ? `${b.plus_grosses[0].nom} — ${poids(b.plus_grosses[0].octets)}`
                : '—'} t={t} />
          </>} />

          <Panneau Icon={HardDrive} titre="Disque du serveur" couleur={t.primary} t={t} enfants={<>
            {d?.erreur ? (
              <Ligne libelle="Lecture" valeur={d.erreur} ton="danger" t={t} />
            ) : (
              <>
                <Ligne libelle="Total" valeur={poids(d.total_octets)} t={t} />
                <Ligne libelle="Libre" valeur={poids(d.libre_octets)}
                  ton={d.utilise_pourcent > 85 ? 'danger' : 'succes'} t={t} />
                <Ligne libelle="Occupation" valeur={`${d.utilise_pourcent} %`}
                  ton={d.utilise_pourcent > 85 ? 'danger' : undefined} t={t} />
                <div style={{ height: 8, borderRadius: 99, background: t.bgInput, overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(d.utilise_pourcent || 0, 100)}%`, height: '100%',
                    background: d.utilise_pourcent > 85 ? t.danger : t.primary }} />
                </div>
                <p style={{ margin: 0, fontSize: 12, color: t.textMuted }}>
                  Disque partagé avec KLASYS, Daniced Pay et le site vitrine.
                </p>
              </>
            )}
          </>} />

          <Panneau Icon={DatabaseBackup} titre="Sauvegardes" couleur={t.primary} t={t}
            coin={sv?.visible && (
              <span style={{ fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 99,
                background: sv.fraiche ? t.successSoft : t.dangerSoft,
                color: sv.fraiche ? t.successText : t.dangerText }}>
                {sv.fraiche ? 'à jour' : 'en retard'}
              </span>
            )}
            enfants={<>
              {!sv?.visible ? (
                <Ligne libelle="Lecture" valeur={sv?.raison || 'indisponible'} ton="danger" t={t} />
              ) : (
                <>
                  <Ligne libelle="Dernière" valeur={quand(sv.derniere_le)} t={t} />
                  <Ligne libelle="Taille" valeur={`${poids(sv.taille_octets)} · ${sv.fichiers} fichier(s)`} t={t} />
                  <Ligne libelle="Jeux conservés" valeur={sv.nombre} t={t} />
                  <p style={{ margin: 0, fontSize: 12, color: t.textMuted, lineHeight: 1.55 }}>
                    Sauvegarde quotidienne à 2 h, lue ici en lecture seule. La restauration se fait
                    depuis le serveur, pas depuis cet écran.
                  </p>
                </>
              )}
            </>} />
        </div>
      )}

      {/* Structure de la base */}
      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
        <div style={{ padding: '13px 18px', borderBottom: `1px solid ${t.borderLight}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 15,
            fontWeight: 700, color: t.text }}>
            <ListChecks size={16} style={{ color: t.primary }} />
            Structure de la base
            <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>
              {migrations.length} migration(s)
              {enAttente ? ` · ${enAttente} en attente` : ''}
              {enEchec ? ` · ${enEchec} en échec` : ''}
            </span>
          </span>
          <button type="button" onClick={appliquer} disabled={application || (!enAttente && !enEchec)}
            style={{ background: (enAttente || enEchec) ? t.primary : t.bgInput,
              color: (enAttente || enEchec) ? '#fff' : t.textMuted,
              border: `1px solid ${(enAttente || enEchec) ? t.primary : t.border}`,
              borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600,
              cursor: (enAttente || enEchec) ? 'pointer' : 'default', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <Play size={13} />
            {application ? 'Application…' : 'Appliquer ce qui manque'}
          </button>
        </div>

        {message && (
          <p style={{ margin: 0, padding: '12px 18px', fontSize: 13,
            color: message.startsWith('Échec') ? t.dangerText : t.successText,
            borderBottom: `1px solid ${t.borderLight}` }}>{message}</p>
        )}

        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {migrations.map((m) => {
              const Icone = m.statut === 'applique' ? CheckCircle2
                : m.statut === 'echec' ? AlertTriangle : Clock;
              const couleur = m.statut === 'applique' ? t.successText
                : m.statut === 'echec' ? t.dangerText : t.warningText;
              return (
                <tr key={m.nom} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <td style={{ padding: '11px 18px', width: 30 }}>
                    <Icone size={15} style={{ color: couleur }} />
                  </td>
                  <td style={{ padding: '11px 0', fontSize: 13, color: t.text }}>
                    {m.nom}
                    {m.erreur && (
                      <span style={{ display: 'block', fontSize: 12, color: t.dangerText, marginTop: 3 }}>
                        {m.erreur}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '11px 18px', fontSize: 12, color: t.textMuted,
                    textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {m.statut === 'applique' ? `${m.applique_le} · ${m.duree_ms ?? '—'} ms`
                      : m.statut === 'echec' ? 'en échec' : 'en attente'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Jeux de sauvegarde */}
      {jeux?.visible && (
        <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
          overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: `1px solid ${t.borderLight}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, fontSize: 15,
              fontWeight: 700, color: t.text }}>
              <DatabaseBackup size={16} style={{ color: t.primary }} />
              Jeux de sauvegarde
              <span style={{ fontSize: 12, fontWeight: 600, color: t.textMuted }}>
                {jeux.jeux.length} conservé(s) · {jeux.dossier}
              </span>
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {jeux.jeux.slice(0, 10).map((j) => (
                <tr key={j.nom} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <td style={{ padding: '10px 18px', fontSize: 13, color: t.text, whiteSpace: 'nowrap' }}>
                    {quand(j.le)}
                  </td>
                  <td style={{ padding: '10px 0', fontSize: 12, color: t.textMuted }}>
                    {j.fichiers.map((f) => f.nom).join(' · ')}
                  </td>
                  <td style={{ padding: '10px 18px', fontSize: 13, color: t.textSecondary,
                    textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {poids(j.taille_octets)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ padding: '14px 18px', borderTop: `1px solid ${t.borderLight}` }}>
            <p style={{ margin: '0 0 8px', fontSize: 13, color: t.textSecondary, lineHeight: 1.6 }}>
              La restauration ne se déclenche pas depuis cet écran : réécrire une base de
              production se fait sur le serveur, les yeux sur la commande. La voici, à compléter
              avec le jeu voulu :
            </p>
            <code style={{ display: 'block', background: t.bgInput, border: `1px solid ${t.border}`,
              borderRadius: 8, padding: '10px 12px', fontSize: 12, color: t.text,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', wordBreak: 'break-all' }}>
              {jeux.commande_restauration}
            </code>
          </div>
        </div>
      )}

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        Les migrations s&rsquo;appliquent aussi au démarrage du serveur. Une migration en échec
        arrête les suivantes : c&rsquo;est voulu, elles se supposent les unes les autres.
      </p>
    </div>
  );
};

export default Exploitation;
